import _ from 'lodash'
import { useLocalStorage } from '@vueuse/core'
import {
    createConversation,
    sortConversation,
    createInform
} from '@/utils/handleSomeData'
import Message from './message'
import { ImClient } from '@/IM/imClient'
import { informType, messageType } from '@/constant'
import { useRouter, useRoute } from 'vue-router'
import { sessionListList } from '@/api/custom'
/* route */
const route = useRoute()
/* router */
const router = useRouter()
const { INFORM_FROM } = informType
const { CHAT_TYPE } = messageType

// 追踪已清除未读数的会话（持久化到 localStorage）
function getClearedUnreadSessions() {
    const storageId = ImClient.user
    return JSON.parse(localStorage.getItem(`ZHWIM_${storageId}_clearedUnreadSessions`) || '{}')
}

function setSessionCleared(key) {
    const storageId = ImClient.user
    const cleared = getClearedUnreadSessions()
    cleared[key] = true
    localStorage.setItem(`ZHWIM_${storageId}_clearedUnreadSessions`, JSON.stringify(cleared))
}

function isSessionCleared(key) {
    const cleared = getClearedUnreadSessions()
    return cleared[key] === true
}

// 保存未读数到 localStorage（state: 当前会话数据，payload: 新增/更新的会话）
function saveUnreadMapToStorage(state, payload) {
    const storageId = ImClient.user
    const unreadMap = JSON.parse(localStorage.getItem(`ZHWIM_${storageId}_unreadMap`) || '{}')
    
    // 保存当前会话列表中的未读数（同时检查 conversationListData 和 allConversationList）
    Object.keys(state.conversationListData).forEach(key => {
        // 如果会话已被用户清除，则不保存未读数
        if (isSessionCleared(key)) {
            return
        }
        const unread = state.conversationListData[key].unreadMessageNum
        if (unread > 0) {
            unreadMap[key] = unread
        } else if (unreadMap[key] !== undefined) {
            // 如果当前值是 0，删除保存的未读数
            delete unreadMap[key]
        }
    })
    
    // 同时处理 allConversationList
    if (state.allConversationList) {
        Object.keys(state.allConversationList).forEach(key => {
            // 如果会话已被用户清除，则不保存未读数
            if (isSessionCleared(key)) {
                return
            }
            const unread = state.allConversationList[key].unreadMessageNum
            if (unread > 0) {
                unreadMap[key] = unread
            } else if (unreadMap[key] !== undefined) {
                // 如果当前值是 0，删除保存的未读数
                delete unreadMap[key]
            }
        })
    }
    
    // 如果有 payload，保存 payload 中新增/更新会话的未读数
    if (payload) {
        Object.keys(payload).forEach(key => {
            // 如果该会话已被用户清除，则不保存未读数（由 UPDATE_CONVERSATION_LIST 处理标记）
            if (isSessionCleared(key)) {
                delete unreadMap[key]
                return
            }
            const unread = payload[key].unreadMessageNum
            if (unread > 0) {
                unreadMap[key] = unread
            }
        })
    }
    
    console.log('>>>>>>>[DEBUG] 保存未读数到localStorage', {
        storageId,
        totalCount: Object.keys(unreadMap).length,
        unreadMap
    })
    try {
        localStorage.setItem(`ZHWIM_${storageId}_unreadMap`, JSON.stringify(unreadMap))
        console.log('>>>>>>>[DEBUG] 保存成功')
    } catch (e) {
        console.error('>>>>>>>[DEBUG] 保存失败:', e)
    }
}

function getGroupCategory(data) {
    try {
        // 1. 提取第一个会话的data（因为数据是对象形式，key是会话ID）
        const conversationId = Object.keys(data)[0]
        const conversationData = data[conversationId]
        if (conversationData?.latestSendTime) {
            const messageDate = new Date(conversationData.latestSendTime)
            const today = new Date()
            const isToday =
                messageDate.getFullYear() === today.getFullYear() &&
                messageDate.getMonth() === today.getMonth() &&
                messageDate.getDate() === today.getDate()
            return {
                isToday,
                createTime: conversationData.latestSendTime,
                createDate: messageDate
            }
        }

        // 2. 提取latestMessage中的ext.msgExt并解析JSON
        const msgExtStr = conversationData?.latestMessage?.ext?.msgExt
        if (!msgExtStr) {
            console.log('未找到msgExt字段')
            return undefined
        }

        const msgExt = JSON.parse(msgExtStr)
        const groupCategory = msgExt.groupCategory

        // 3. 返回 groupCategory
        return groupCategory
    } catch (err) {
        // 解析失败不抛错，保证程序稳定
        console.warn('获取 groupCategory 失败:', err)
        return undefined
    }
}
// 检查群组创建时间是否为今日
function checkGroupCreateTime(data) {
    try {
        // 1. 提取第一个会话的data（因为数据是对象形式，key是会话ID）
        const conversationId = Object.keys(data)[0]
        const conversationData = data[conversationId]
        const directCreateTime = conversationData?.groupCreateTime
        if (directCreateTime) {
            return {
                isToday: isTodayTime(directCreateTime),
                createTime: directCreateTime,
                createDate: new Date(directCreateTime)
            }
        }

        // 2. 提取latestMessage中的ext.msgExt并解析JSON
        const msgExtStr = conversationData?.latestMessage?.ext?.msgExt
        if (!msgExtStr) {
            console.log('未找到msgExt字段')
            return { isToday: false, createTime: null }
        }

        const msgExt = JSON.parse(msgExtStr)
        const groupCreateTime = msgExt.groupCreateTime

        // 验证时间是否有效
        if (!groupCreateTime) {
            console.log('groupCreateTime格式错误或不存在')
            return { isToday: false, createTime: null }
        }

        // 3. 转换时间戳并判断是否为今日
        const createDate = new Date(groupCreateTime)

        return {
            isToday: isTodayTime(groupCreateTime),
            createTime: groupCreateTime,
            createDate: createDate // 方便调试的格式化日期
        }
    } catch (error) {
        console.error('解析群创建时间出错:', error)
        return { isToday: false, createTime: null }
    }
}

function isTodayTime(value) {
    if (!value) return false
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return false
    const now = new Date()
    return (
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
    )
}

function conversationGroupCreateTime(item) {
    if (!item || item.conversationType !== 'groupChat') return null
    if (item.groupCreateTime) return item.groupCreateTime
    const msgExtStr = item.latestMessage?.ext?.msgExt
    if (msgExtStr) {
        try {
            const msgExt = typeof msgExtStr === 'string' ? JSON.parse(msgExtStr) : msgExtStr
            if (msgExt.groupCreateTime) return msgExt.groupCreateTime
        } catch (e) {
            return null
        }
    }
    return null
}

function conversationGroupCategory(item) {
    if (!item) return undefined
    if (item.groupCategory !== undefined) return item.groupCategory
    const msgExtStr = item.latestMessage?.ext?.msgExt
    if (!msgExtStr) return undefined
    try {
        const msgExt = typeof msgExtStr === 'string' ? JSON.parse(msgExtStr) : msgExtStr
        return msgExt.groupCategory
    } catch (e) {
        return undefined
    }
}

function conversationMatchesScope(item, scope = {}) {
    if (!item) return false
    if (!scope || scope.type === 'all') return true
    if (scope.type === 'single') return item.conversationType === 'singleChat'
    if (scope.type === 'group') return item.conversationType === 'groupChat'
    if (scope.type === 'today') {
        return item.conversationType === 'groupChat' && isTodayTime(conversationGroupCreateTime(item))
    }
    if (scope.type === 'category') {
        return item.conversationType === 'groupChat' && String(conversationGroupCategory(item)) === String(scope.groupCategory)
    }
    return true
}

function filterConversationsByScope(all, scope) {
    const filtered = {}
    Object.keys(all || {}).forEach((key) => {
        if (conversationMatchesScope(all[key], scope)) {
            filtered[key] = all[key]
        }
    })
    return sortConversation(_.cloneDeep(filtered))
}

const Conversation = {
    state: {
        informDetail: [],
        conversationListData: {},
        allConversationList: {},  // 保存所有会话（不区分分类）
        currentConversationScope: { id: '2', name: '私聊', type: 'single' }
    },
    mutations: {
        SET_CONVERSATION_SCOPE: (state, scope) => {
            state.currentConversationScope = scope || { id: '1', name: '全部', type: 'all' }
            state.conversationListData = filterConversationsByScope(state.allConversationList, state.currentConversationScope)
        },
        UPDATE_CONVERSATION_GROUP_CATEGORY: (state, payload) => {
            const key = payload?.key
            if (!key) return
            const groupCategory = payload.groupCategory
            if (state.allConversationList[key]) {
                state.allConversationList[key].groupCategory = groupCategory
            }
            if (state.conversationListData[key]) {
                state.conversationListData[key].groupCategory = groupCategory
            }
            state.conversationListData = filterConversationsByScope(
                state.allConversationList,
                state.currentConversationScope
            )
            const storageId = ImClient.user
            localStorage.setItem(
                `ZHWIM_${storageId}_allConversationList`,
                JSON.stringify(state.allConversationList)
            )
        },
        //初始化会话列表的数据（根据登陆的id取其对应的会话数据）
        INIT_CONVERSATION_STATE: (state, payload) => {
            // 支持新的 payload 格式（带分页信息和未读数保护）
            // 处理旧格式调用（App.vue 等直接调用）或无参数的情况
            if (!payload || typeof payload !== 'object') {
                console.log('>>>>>>>[DEBUG] INIT_CONVERSATION_STATE 被调用但没有有效数据')
                return
            }
            const temp = payload.tempObj || payload
            const isPageLoad = payload.isPageLoad || false
            const existingUnreadMap = payload.existingUnreadMap || {}
            const replaceCurrent = payload.replaceCurrent !== false
            const preserveServerScope = payload.preserveServerScope === true
            
            if (!temp || typeof temp !== 'object' || Object.keys(temp).length === 0) {
                console.log('>>>>>>>[DEBUG] INIT_CONVERSATION_STATE temp 为空')
                if (replaceCurrent) {
                    state.conversationListData = {}
                    const storageId = ImClient.user
                    localStorage.setItem(
                        `ZHWIM_${storageId}_conversationList`,
                        JSON.stringify({})
                    )
                }
                return
            }
            
            console.log('%c Line:27 🍉 temp', 'color:#b03734', temp)
            //   console.log('Object.keys(temp).length',temp.length)
            const storageId = ImClient.user
            state.informDetail = useLocalStorage(
                `ZHWIM_${storageId}_INFORM`,
                []
            )
            const sortedData = sortConversation(_.cloneDeep(temp))
            console.log('>>>>>>>[DEBUG] sortedData 未读数:', Object.fromEntries(
                Object.keys(sortedData).map(key => [key, sortedData[key].unreadMessageNum])
            ))
            // 分页加载时，用 existingUnreadMap 覆盖 sortedData 中的未读数（避免服务端数据覆盖本地已清除的状态）
            if (isPageLoad) {
                Object.keys(sortedData).forEach(key => {
                    // 使用 in 操作符检查，确保即使是 0 值也会被覆盖
                    if (key in existingUnreadMap) {
                        sortedData[key].unreadMessageNum = existingUnreadMap[key]
                        console.log(`>>>>>>>[DEBUG] 用 existingUnreadMap 覆盖 sortedData 会话 ${key} 的未读数: ${existingUnreadMap[key]}`)
                    }
                })
            }
            console.log('>>>>>>>[DEBUG] 覆盖后 sortedData 未读数:', Object.fromEntries(
                Object.keys(sortedData).map(key => [key, sortedData[key].unreadMessageNum])
            ))
            // 合并到 allConversationList（保存所有会话）
            state.allConversationList = _.assign(
                _.cloneDeep(state.allConversationList),
                sortedData
            )
            if (preserveServerScope) {
                state.conversationListData = replaceCurrent
                    ? sortedData
                    : sortConversation(
                          _.assign(
                              _.cloneDeep(state.conversationListData),
                              sortedData
                          )
                      )
            } else {
                state.conversationListData = replaceCurrent
                    ? sortedData
                    : _.assign(state.conversationListData, sortedData)
                state.conversationListData = filterConversationsByScope(
                    state.allConversationList,
                    state.currentConversationScope
                )
            }
            console.log('>>>>>>>[DEBUG] _.assign 后 state.conversationListData 未读数:', Object.fromEntries(
                Object.keys(state.conversationListData).map(key => [key, state.conversationListData[key]?.unreadMessageNum])
            ))
            console.log('>>>>>>>[DEBUG] _.assign 后 state.allConversationList 未读数:', Object.fromEntries(
                Object.keys(state.allConversationList).map(key => [key, state.allConversationList[key]?.unreadMessageNum])
            ))
            localStorage.setItem(
                `ZHWIM_${storageId}_conversationList`,
                JSON.stringify(sortedData)
            )
            localStorage.setItem(
                `ZHWIM_${storageId}_allConversationList`,
                JSON.stringify(state.allConversationList)
            )

            // 从 localStorage 恢复未读数
            const unreadMap = JSON.parse(localStorage.getItem(`ZHWIM_${storageId}_unreadMap`) || '{}')
            console.log('>>>>>>>[DEBUG] 恢复未读数 from localStorage', {
                storageId,
                unreadMapKeys: Object.keys(unreadMap),
                sortedDataKeys: Object.keys(sortedData),
                isPageLoad,
                existingUnreadMap
            })
            
            let restoredCount = 0
            Object.keys(sortedData).forEach(key => {
                // 如果会话已被用户清除，则强制设为 0，跳过恢复
                if (isSessionCleared(key)) {
                    if (state.conversationListData[key]) {
                        state.conversationListData[key].unreadMessageNum = 0
                    }
                    if (state.allConversationList[key]) {
                        state.allConversationList[key].unreadMessageNum = 0
                    }
                    console.log(`>>>>>>>[DEBUG] 会话 ${key} 已被清除，跳过恢复未读数`)
                    return
                }
                // 分页加载时，优先使用 existingUnreadMap（已清除的未读数状态，包括0值）
                if (isPageLoad && existingUnreadMap.hasOwnProperty(key)) {
                    if (state.conversationListData[key]) {
                        state.conversationListData[key].unreadMessageNum = existingUnreadMap[key]
                    }
                    if (state.allConversationList[key]) {
                        state.allConversationList[key].unreadMessageNum = existingUnreadMap[key]
                    }
                    console.log(`>>>>>>>[DEBUG] 分页恢复会话 ${key} 的未读数: ${existingUnreadMap[key]}`)
                    restoredCount++
                } else if (unreadMap[key] !== undefined) {
                    // 优先使用 localStorage 保存的未读数（已清除的状态）
                    if (state.conversationListData[key]) {
                        state.conversationListData[key].unreadMessageNum = unreadMap[key]
                    }
                    if (state.allConversationList[key]) {
                        state.allConversationList[key].unreadMessageNum = unreadMap[key]
                    }
                    restoredCount++
                    console.log(`>>>>>>>[DEBUG] 恢复会话 ${key} 的未读数: ${unreadMap[key]}`)
                }
            })
            console.log(`>>>>>>>[DEBUG] 共恢复了 ${restoredCount} 个会话的未读数`)

            // state.conversationListData = useLocalStorage(
            //     `ZHWIM_${storageId}_conversationList`,
            //     {}
            // )
            // console.log("%c Line:62 🌮 会话列表>>>>>>>>>>>>state.conversationListData", "color:#33a5ff", state.conversationListData);
        },
        //清空系统通知
        CLEAR_INFORM_LIST: (state) => {
            state.informDetail = []
        },
        CLEAR_INFORM_LIST_INFO_NEW: (state) => {
            state.conversationListData = {}
        },
        //更新系统通知
        UPDATE_INFORM_LIST: (state, informBody) => {
            const toBeUpdateInform = _.assign([], state.informDetail)
            // let _index = toBeUpdateInform.findIndex(
            //   (v) => v.from === informBody.from
            // );
            // if (_index === -1) {
            //   toBeUpdateInform.unshift(informBody);
            // } else {
            //   toBeUpdateInform.splice(_index, 1);
            //   toBeUpdateInform.unshift(informBody);
            // }
            toBeUpdateInform.unshift(informBody)
            state.informDetail = toBeUpdateInform
        },
        //更新已有会话
        UPDATE_CONVERSATION_LIST: (state, payload) => {
            if (!payload || Object.keys(payload).length === 0) {
                return
            }
            Object.keys(payload).forEach((key) => {
                const oldItem = state.allConversationList[key] || state.conversationListData[key] || {}
                if (!payload[key].groupCreateTime && oldItem.groupCreateTime) {
                    payload[key].groupCreateTime = oldItem.groupCreateTime
                }
                if (payload[key].groupCategory === undefined && oldItem.groupCategory !== undefined) {
                    payload[key].groupCategory = oldItem.groupCategory
                }
                if (!payload[key].groupName && oldItem.groupName) {
                    payload[key].groupName = oldItem.groupName
                }
                if (!payload[key].conversationInfo && oldItem.conversationInfo) {
                    payload[key].conversationInfo = oldItem.conversationInfo
                }
            })
            // const { conversationType, conversationKey } = payload
            // const data = {
            //     zhw1722499045780: {
            //         isTop: 2,
            //         conversationType: 'singleChat'
            //         // ... 其他属性
            //     }
            // }

            // 获取第一个键名（因为外层只有一个键）
            const outerKey = Object.keys(payload)[0]
            const conversationType = payload[outerKey].conversationType
            console.log('.....conversationType', state, conversationType)

            const chatTypeInfo = localStorage.getItem('chatTypeInfo') || 1
            const groupCategoryInfo =
                localStorage.getItem('groupCategoryInfo') || null
            console.log('chatTypeInfo', chatTypeInfo)

            // 关键修复：在 _.assign 之前，先保存原始未读数状态
            const originalUnreadMap = {}
            Object.keys(payload).forEach(key => {
                originalUnreadMap[key] = state.allConversationList[key]?.unreadMessageNum ?? 
                                         state.conversationListData[key]?.unreadMessageNum ?? 0
            })
            
            // 同时更新 allConversationList（保存所有会话，不区分分类）
            state.allConversationList = _.assign(
                _.cloneDeep(state.allConversationList),
                payload
            )
            state.conversationListData = filterConversationsByScope(state.allConversationList, state.currentConversationScope)

            if (state.currentConversationScope) {
                console.log('>>>>>>>按当前本地分区更新会话', state.currentConversationScope)
            } else if (
                chatTypeInfo == 2 &&
                conversationType == 'groupChat' &&
                !groupCategoryInfo
            ) {
                console.log('>>>>>>>开始更新会话(群聊分组-群聊)', payload)
                const sortedData = sortConversation(
                    _.assign(_.cloneDeep(state.conversationListData), payload)
                )
                state.conversationListData = sortedData
            } else if (chatTypeInfo == 1 && conversationType == 'singleChat') {
                console.log('>>>>>>>开始更新会话(私聊分组-私聊)', payload)
                const sortedData = sortConversation(
                    _.assign(_.cloneDeep(state.conversationListData), payload)
                )
                state.conversationListData = sortedData
            } else if (chatTypeInfo == 2 && conversationType == 'singleChat') {
                console.log('>>>>>>>跳过更新会话(群聊分组-私聊)')
            } else if (chatTypeInfo == 1 && conversationType == 'groupChat') {
                console.log('>>>>>>>跳过更新会话(私聊分组-群聊)')
            } else if (chatTypeInfo == 3) {
                // 今日分组：检查群组创建时间，只更新今日的群聊消息，不更新私聊消息
                if (conversationType == 'groupChat') {
                    const checkResult = checkGroupCreateTime(payload)
                    console.log('>>>>>>>今日分组群聊检查结果:', checkResult)
                    if (checkResult.isToday) {
                        console.log(
                            '>>>>>>>开始更新会话(今日分组-今日群聊)',
                            payload
                        )
                        const sortedData = sortConversation(
                            _.assign(
                                _.cloneDeep(state.conversationListData),
                                payload
                            )
                        )
                        state.conversationListData = sortedData
                    } else {
                        console.log('>>>>>>>跳过更新会话(今日分组-非今日群聊)')
                    }
                } else {
                    console.log('>>>>>>>跳过更新会话(今日分组-私聊)')
                }
            } else if (
                chatTypeInfo == 2 &&
                conversationType == 'groupChat' &&
                groupCategoryInfo &&
                groupCategoryInfo != 'null' &&
                groupCategoryInfo != 'undefined' &&
                String(groupCategoryInfo) == String(getGroupCategory(payload))
            ) {
                // 群聊分组模式：只更新匹配的群聊
                const res = getGroupCategory(payload)
                console.log(
                    '>>>>>>>分组群聊检查结果:',
                    '成功了亲爱的!!!',
                    res
                )
                const sortedData = sortConversation(
                    _.assign(
                        _.cloneDeep(state.conversationListData),
                        payload
                    )
                )
                state.conversationListData = sortedData
            } else if (
                chatTypeInfo == 2 &&
                conversationType == 'groupChat' &&
                groupCategoryInfo &&
                groupCategoryInfo != 'null' &&
                groupCategoryInfo != 'undefined'
            ) {
                // 群聊分组模式：不匹配的群聊不更新，直接跳过
                console.log('>>>>>>>跳过更新会话(群聊分组-不匹配的群聊)', payload)
            } else {
                console.log('>>>>>>>开始更新会话(默认/其他)', payload)
                const res = getGroupCategory(payload)
                console.log('>>>>>>>分组群聊检查结果:', '成功了亲爱的!!!', res)
                const sortedData = sortConversation(
                    _.assign(_.cloneDeep(state.conversationListData), payload)
                )
                state.conversationListData = sortedData
            }
            console.log('>>>>>>>[DEBUG] UPDATE_CONVERSATION_LIST 结束，准备保存未读数')
            
            // 关键修复：如果会话已被用户清除，且 payload 未读数 <= 原始未读数（没有新消息），则强制设为 0
            Object.keys(payload).forEach(key => {
                if (isSessionCleared(key)) {
                    const payloadUnread = payload[key].unreadMessageNum || 0
                    const originalUnread = originalUnreadMap[key] || 0
                    
                    // 如果 payload 未读数 <= 原始未读数，说明没有新消息，强制设为 0
                    if (payloadUnread <= originalUnread) {
                        if (state.conversationListData[key]) {
                            state.conversationListData[key].unreadMessageNum = 0
                        }
                        if (state.allConversationList[key]) {
                            state.allConversationList[key].unreadMessageNum = 0
                        }
                        console.log(`>>>>>>>[DEBUG] UPDATE_CONVERSATION_LIST 中强制设 ${key} 未读数为 0 (payload=${payloadUnread}, original=${originalUnread})`)
                    } else {
                        // 有新消息，清除已清除标记
                        const cleared = getClearedUnreadSessions()
                        delete cleared[key]
                        localStorage.setItem(`ZHWIM_${ImClient.user}_clearedUnreadSessions`, JSON.stringify(cleared))
                        console.log(`>>>>>>>[DEBUG] 会话 ${key} 收到新消息，清除已清除标记`)
                    }
                }
            })
            
            // 保存未读数到 localStorage
            saveUnreadMapToStorage(state, payload)
        },
        //删除某条会话
        DELETE_ONE_CONVERSATION: (state, key) => {
            console.log('>>>>>>>执行删除会话操作', key)
            const toUpdateConversation = _.assign(
                {},
                state.conversationListData
            )
            if (toUpdateConversation[key]) {
                delete toUpdateConversation[key]
            }
            console.log('删除后toUpdateConversation', toUpdateConversation)
            state.conversationListData = _.assign({}, toUpdateConversation)
            const allConversationList = _.assign({}, state.allConversationList)
            if (allConversationList[key]) {
                delete allConversationList[key]
            }
            state.allConversationList = allConversationList
            // 从 localStorage 中删除该会话的未读数
            const storageId = ImClient.user
            const unreadMap = JSON.parse(localStorage.getItem(`ZHWIM_${storageId}_unreadMap`) || '{}')
            delete unreadMap[key]
            localStorage.setItem(`ZHWIM_${storageId}_unreadMap`, JSON.stringify(unreadMap))
            const cleared = getClearedUnreadSessions()
            delete cleared[key]
            localStorage.setItem(`ZHWIM_${storageId}_clearedUnreadSessions`, JSON.stringify(cleared))
        },
        //清除会话未读状态
        CLEAR_UNREAD_NUM: (state, key) => {
            state.conversationListData[key].unreadMessageNum = 0
            // 同时更新 allConversationList
            if (state.allConversationList[key]) {
                state.allConversationList[key].unreadMessageNum = 0
            }
            // 标记该会话已被用户清除（持久化，用于阻止后续 gatherConversation 等操作重新设置未读数）
            setSessionCleared(key)
            console.log(`>>>>>>>[DEBUG] 标记会话 ${key} 为已清除`)
            // 重新保存未读数（会自动处理 key 对应的 0 值）
            saveUnreadMapToStorage(state)
        },
        //清除信息卡片未读
        CLEAR_UNTREATED_STATUS: (state, index) => {
            console.log('>>>>>执行清除卡片未读', index)
            state.informDetail[index].untreated = 0
        },
        //更改卡片消息的按钮状态
        UPDATE_INFORM_BTNSTATUS: (state, { index: index, btnStatus }) => {
            console.log('>>>>触发了按钮更新状态', index, btnStatus)
            state.informDetail[index].operationStatus = btnStatus
        }
    },
    actions: {
        // 初始化会话列表
        getSessionList: async ({ commit, state }, userId) => {
            console.log('userId', userId)
            let tempObj = {}
            // 分页加载时保存当前已存在会话的未读数（避免清除后又被覆盖）
            const existingUnreadMap = {}
            if (userId.pageNum > 1) {
                // 保存当前未读数状态（从 conversationListData 和 allConversationList 中都要保存）
                const allKeys = new Set([
                    ...Object.keys(state.conversationListData),
                    ...Object.keys(state.allConversationList)
                ])
                allKeys.forEach(key => {
                    const unread = state.conversationListData[key]?.unreadMessageNum ?? 
                                   state.allConversationList[key]?.unreadMessageNum ?? 0
                    existingUnreadMap[key] = unread
                })
                console.log('>>>>>>>[DEBUG] 分页加载，保存现有未读数状态:', existingUnreadMap)
            }
            try {
                let obj = {
                    formUser: userId.userId,
                    pageNum: userId.pageNum,
                    pageSize: userId.pageSize
                }
                if (userId.msgType != 0) {
                    obj.msgType = userId.msgType
                }
                // 支持 groupCategory 参数（今日群聊）
                if (userId.groupCategory !== undefined) {
                    obj.groupCategory = userId.groupCategory
                }
                if (userId.today) {
                    obj.today = 1
                }
                let { rows } = await sessionListList(obj)
                if (obj.msgType == 1 || obj.msgType == 2) {
                    rows = (rows || []).filter((item) => String(item.msgType) === String(obj.msgType))
                }
                if (obj.groupCategory !== undefined) {
                    rows = (rows || []).filter((item) => String(item.groupCategory) === String(obj.groupCategory))
                }
                console.log('会话列表接口返回数据:', rows)
                console.log('>>>>>>>[DEBUG] 构建 tempObj 前 existingUnreadMap:', existingUnreadMap)
                if (rows && rows.length) {
                    for (let i = 0; i < rows.length; i++) {
                        let item = rows[i]
                        let msgtxt = item.type == 'text' ? item.content : ''
                        let type = 'txt'
                        if (item.type == 'custom') {
                            type = 'custom'
                            if (item.customType == 'image') {
                                msgtxt = '[图片]'
                            } else if (item.customType == 'video') {
                                msgtxt = '[视频]'
                            } else if (item.customType == 'goods') {
                                msgtxt = '[商品]'
                            } else if (item.customType == 'audio') {
                                msgtxt = '[语音]'
                            } else {
                                msgtxt = '[其他]'
                            }
                        }

                        // 尝试获取 groupCategory（可能在 item.groupCategory 或 item.msgExt.groupCategory）
                        let groupCategory = item.groupCategory
                        if (groupCategory === undefined && item.msgExt) {
                            try {
                                const msgExt = typeof item.msgExt === 'string' ? JSON.parse(item.msgExt) : item.msgExt
                                groupCategory = msgExt.groupCategory
                            } catch (e) {}
                        }

                        // 分页加载时，确定未读数（优先使用已保存的状态）
                        let unreadNum = item.unreadMessageNum || 0
                        if (userId.pageNum > 1) {
                            // 确定 sessionId/key
                            let sessionKey = item.msgType == 2 ? item.sessionId : 
                                (item.formUser == userId.userId ? item.toUser : item.formUser)
                            if (sessionKey in existingUnreadMap) {
                                unreadNum = existingUnreadMap[sessionKey]
                                console.log(`>>>>>>>[DEBUG] 用 existingUnreadMap 覆盖会话 ${sessionKey} 的未读数: ${unreadNum}`)
                            }
                        }

                        if (item.msgType == 2) {
                            // 群组
                            const groupName = item.groupName || item.title || item.sessionId
                            tempObj[item.sessionId] = {
                                id: item.id,
                                sessionId: item.sessionId,
                                isTop: item.isTop || (item.top ? 1 : 2),
                                conversationType: 'groupChat',
                                conversationKey: item.sessionId,
                                groupCategory: groupCategory,  // 保存 groupCategory
                                groupCreateTime: item.groupCreateTime || item.createTime || null,
                                conversationInfo: {
                                    name: groupName,
                                    avatarUrl:
                                        'https://img.zhwapp.com/im/groupicon.png'
                                },
                                fromInfo: {
                                    fromId: item.formUser,
                                    fromName: item.fromNickName || item.formUser || ''
                                },
                                targetId: item.sessionId,
                                unreadMessageNum: unreadNum,
                                latestMessage: {
                                    msg: msgtxt,
                                    type: type,
                                    ext: {
                                        timeout: 1,
                                        msgExt: item.msgExt || null  // 保存原始 msgExt
                                    }
                                },
                                latestMessageId: item.messageId,
                                latestSendTime: item.updateTime
                                    ? new Date(item.updateTime).getTime()
                                    : new Date(item.createTime).getTime(),
                                groupName
                            }
                        } else {
                            // 单聊
                            let targetId =
                                item.formUser == userId.userId
                                    ? item.toUser
                                    : item.formUser
                            //  console.log('i',i)
                            //  console.log("%c Line:154 🍇 targetId", "color:#e41a6a", targetId);
                            tempObj[targetId] = {
                                id: item.id,
                                sessionId: item.sessionId,
                                isTop: item.isTop || (item.top ? 1 : 2),
                                conversationType: 'singleChat',
                                conversationKey: targetId,
                                conversationInfo: {
                                    // 去旧IM：Go 后端返回已 enrich 的 title/avatar(对方昵称头像)，优先用；兜底原字段
                                    name:
                                        item.title ||
                                        (item.formUser == userId.userId
                                            ? item.toNickName
                                            : item.fromNickName),
                                    avatarUrl:
                                        item.avatar ||
                                        (item.formUser == userId.userId
                                            ? item.toAvatar
                                            : item.fromAvatar)
                                },
                                fromInfo: {
                                    fromId: item.formUser,
                                    fromName: item.fromNickName
                                },
                                targetId: targetId,
                                unreadMessageNum: unreadNum,
                                latestMessage: {
                                    msg: msgtxt,
                                    type: type,
                                    ext: {
                                        timeout: 1
                                    }
                                },
                                latestMessageId: item.messageId,
                                latestSendTime: item.updateTime
                                    ? new Date(item.updateTime).getTime()
                                    : new Date(item.createTime).getTime()
                            }
                        }
                    }
                }
            } catch (error) {}
            // 传递分页信息和已保存的未读数
            commit('INIT_CONVERSATION_STATE', {
                tempObj,
                isPageLoad: userId.pageNum > 1,
                existingUnreadMap,
                replaceCurrent: userId.pageNum <= 1,
                preserveServerScope:
                    userId.msgType !== undefined ||
                    userId.groupCategory !== undefined ||
                    Boolean(userId.today)
            })
        },
        //添加新系统通知
        createNewInform: ({ dispatch, commit }, params) => {
            const { fromType, informContent } = params
            console.log('>>>>>>>>>createNewInform', fromType, informContent)
            const result = createInform(fromType, informContent)
            commit('UPDATE_INFORM_LIST', result)

            //部分事件需要调用接口更新本地信息或者增加消息内系统通知
            if (fromType === INFORM_FROM.FRIEND) {
                const informMsg = {
                    from: informContent.from,
                    to: informContent.to,
                    chatType: CHAT_TYPE.SINGLE
                }
                switch (informContent.type) {
                    case 'other_person_agree':
                        {
                            //他人同意更新好友列表
                            console.log(
                                '>>>>>【other_person_agree】通知更新好友列表'
                            )
                            dispatch('fetchFriendList')
                        }

                        break
                    case 'unsubscribed':
                        {
                            informMsg.msg = '你俩的友尽了，可重新发起好友申请'
                            dispatch('createInformMessage', informMsg)
                        }
                        break
                    case 'subscribed':
                        {
                            informMsg.msg = '已成为你的好友,开始聊天吧'
                            dispatch('createInformMessage', informMsg)
                        }
                        break
                    default:
                        break
                }
            }
            if (fromType === INFORM_FROM.GROUP) {
                const informMsg = {
                    from: informContent.from,
                    to: informContent.id,
                    chatType: CHAT_TYPE.GROUP
                }
                switch (informContent.operation) {
                    case 'memberPresence': //入群通知
                        {
                            // informContent
                            console.log(
                                '%c Line:245 🥚 入群通知informContent',
                                'color:#ea7e5c',
                                informContent
                            )
                            commit('UPDATE_GROUP_INFOS', {
                                groupId: informContent.id,
                                type: 'addAffiliationsCount'
                            })
                            dispatch('fetchGoupsMember', informContent.id)
                            informMsg.msg = `${informContent.from}加入了群组`
                            dispatch('createInformMessage', informMsg)
                            // dispatch('getGroupPersons', {
                            //   groupId: informContent.id,
                            //   fromHxId: ImClient.user,
                            // })
                        }
                        break
                    case 'memberAbsence':
                        {
                            //退群通知
                            commit('UPDATE_GROUP_INFOS', {
                                groupId: informContent.id,
                                type: 'delAffiliationsCount'
                            })
                            dispatch('fetchGoupsMember', informContent.id)
                            informMsg.msg = `${informContent.from}退出了群组`
                            dispatch('createInformMessage', informMsg)
                            dispatch('getGroupPersons', {
                                groupId: informContent.id,
                                fromHxId: ImClient.user
                            })
                        }
                        break
                    case 'updateAnnouncement':
                        {
                            //更新群公告
                            dispatch('fetchAnnounment', informContent.id)
                            informMsg.msg = `${informContent.from}更新了群组公告，去看看更新的什么吧~`
                            dispatch('createInformMessage', informMsg)
                        }
                        break
                    case 'setAdmin':
                        {
                            dispatch('fetchGoupsAdmin', informContent.id)
                            informMsg.msg = `${informContent.from}设定${informContent.to}为管理员~`
                            dispatch('createInformMessage', informMsg)
                        }
                        break
                    case 'removeAdmin':
                        {
                            dispatch('fetchGoupsAdmin', informContent.id)
                            informMsg.msg = `${informContent.from}移除了${informContent.to}的管理员身份~`
                            dispatch('createInformMessage', informMsg)
                        }
                        break
                    case 'muteMember':
                        {
                            informMsg.msg = `${informContent.from}禁言了${
                                informContent.to ? informContent.to : '你'
                            }~`
                            dispatch('createInformMessage', informMsg)
                        }
                        break
                    case 'unmuteMember':
                        {
                            informMsg.msg = `${informContent.from}取消了${
                                informContent.to ? informContent.to : '你'
                            }的禁言~`
                            dispatch('createInformMessage', informMsg)
                        }
                        break
                    case 'removeMember':
                        {
                            // informMsg.msg = `${informContent.from}将你移出了群组${informContent.id}~`
                            // dispatch('createInformMessage', informMsg)
                            //执行删除会话
                            commit('DELETE_ONE_CONVERSATION', informContent.id)
                            //从群组列表中移除
                            commit('UPDATE_GROUP_LIST', {
                                type: 'deleteFromList',
                                groupId: informContent.id
                            })
                        }
                        break
                    case 'destroy':
                        {
                            informMsg.msg = `${informContent.from}解散了该群~`
                            dispatch('createInformMessage', informMsg)
                            setTimeout(() => {
                                dispatch('fetchGroupList', {
                                    pageNum: 1,
                                    pageSize: 600
                                })
                                // 删除本地会话
                                // 成功，删除本地会话
                                commit(
                                    'DELETE_ONE_CONVERSATION',
                                    informContent.id
                                )
                                // //如果删除的itemKey与当前的message会话页的id一致则跳转至会话默认页。
                                if (
                                    route?.query?.id &&
                                    route.query.id === informContent.id
                                ) {
                                    router.push('/chat/conversation')
                                }
                            }, 300)
                        }
                        break
                    case 'updateInfo':
                        {
                            informMsg.msg = `${informContent.from}更新了群组详情~`
                            dispatch('createInformMessage', informMsg)
                            dispatch('getAssignGroupDetail', informContent.id)
                        }
                        break
                    case 'acceptRequest':
                        {
                            console.log('>>>>>>>收到了群组同意加入事件')
                            setTimeout(() => {
                                dispatch('fetchGroupList', {
                                    pageNum: 1,
                                    pageSize: 600
                                })
                            }, 300)
                        }
                        break
                    case 'changeOwner':
                        {
                            //更换群主 informContent
                            console.log(
                                '%c Line:364 🍊 informContent',
                                'color:#ffdd4d',
                                informContent
                            )
                            dispatch('fetchAnnounment', informContent.id)
                            informMsg.msg = `${informContent.from}变更了群主~`
                            dispatch('createInformMessage', informMsg)
                            dispatch('fetchGoupsMember', informContent.id)
                            dispatch('getGroupPersons', {
                                groupId: informContent.id,
                                fromHxId: ImClient.user
                            })
                        }
                        break
                    default:
                        break
                }
            }
            //memberPresence 群成员加入群组需要进行群组人数+1操作。
            // commit('UPDATE_GROUP_INFOS',{})
        },

        //收集会话依赖数据
        gatherConversation: async ({ commit }, key) => {
            console.log('>>>>>>>[DEBUG] gatherConversation 被调用, key:', key)
            const corresMessage = _.cloneDeep(Message.state.messageList[key])
            console.log('>>>>>>>[DEBUG] gatherConversation corresMessage:', corresMessage)
            const res = await createConversation(corresMessage)
            console.log('>>>>>>>[DEBUG] gatherConversation res:', res)
            commit('UPDATE_CONVERSATION_LIST', res)
        }
    }
}
export default Conversation
